import { Injectable } from '@angular/core';
import { tokenStorageName } from '../../models/resources/common';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Apollo, gql } from 'apollo-angular';
import { ACCEPT_INVITE, LOGIN_MUTATION, PASSWORD_RECOVERY_MUTATION, RECOVERY_LINK_MUTATION } from '../../models/core/graphql.mutations';
import { Subscription } from 'rxjs';
import { GraphqlService } from '../common/graphql.service';
import { INVITE_ID_VALIDATION, INVITE_INFO, LOGIN_TOKEN_SIGNATURE_VALIDATION_QUERY, OLD_CURRENT_USER_QUERY, RECOVERY_TOKEN_SIGNATURE_VALIDATION_QUERY } from '../../models/core/graphql.query';
import { IGraphQLResponse } from '../../models/results/graphql-response.interface';
import { Router } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { FetchResult } from '@apollo/client/link/core/types';
import { Invite } from '../../models/core/invite.interface';
import { FatalError } from '../../models/core/fatal-error.model';

/**
 * Service that manages authorization related logic and operations.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private jwtHelperService: JwtHelperService,
    private router: Router,
    private graphQL: GraphqlService) { }

  /**
   * Gets JWT token to be used for authorization.
   */
  public getAuthorizationToken(): string | null {
    let token = localStorage.getItem(tokenStorageName);
    return token;
  }

  /**
   * Deletes JWT token.
   */
  public deleteAuthorizationToken(): void {
    localStorage.removeItem(tokenStorageName);
  }

  /**
   * Saves JWT token to local storage.
   * @param token
   */
  public setAuthorizationToken(token: string): void {
    localStorage.setItem(tokenStorageName, token);
  }

  /**
   * Gets if current user has been authenticated.
   */
  public isAuthenticated(): boolean {
    // Check if valid token is present in storage.
    let token = this.getAuthorizationToken();
    if (token == null || this.jwtHelperService.isTokenExpired(token) == true) {
      return false;
    }
    return true;
  }

  /**
   * Navigates to logout component to await confirmation
   */
  public navigateToLogout(): void {
    this.router.navigate(['logout']);
  }

  /**
   * Signs current user out of the dashboard.
   */
  public logout(): void {
    this.deleteAuthorizationToken();
  }

  /**
   * Authenticates user with given username and password.
   * @param username
   * @param password
   */
  public async authenticate(username: string, password: string): Promise<FetchResult<IGraphQLResponse<string>>> {
    let result = await this.graphQL.mutate<IGraphQLResponse<string>>(LOGIN_MUTATION, {
      username: username,
      password: password
    });
    return result;
  }

  /**
   * Request recovery link for user with given email address.
   * @param email
   */
  public async requestRecoveryLink(email: string): Promise<FetchResult<IGraphQLResponse<string>>> {
    let result = await this.graphQL.mutate<IGraphQLResponse<string>>(RECOVERY_LINK_MUTATION, {
      email: email
    });
    return result;
  }

  /**
   * Validates recovery signature.
   * @param email
   */
  public async validateRecoverySignature(signature: string): Promise<FetchResult<IGraphQLResponse<string>>> {
    let result = await this.graphQL.query<IGraphQLResponse<string>>(RECOVERY_TOKEN_SIGNATURE_VALIDATION_QUERY, {
      signature: signature
    });
    return result;
  }

  /**
   * Recovers forgotten password.
   * @param password
   * @param signature
   */
  public async recoverPassword(password: string, signature: string): Promise<FetchResult<IGraphQLResponse<string>>> {
    let result = await this.graphQL.mutate<IGraphQLResponse<string>>(PASSWORD_RECOVERY_MUTATION, {
      password: password,
      signature: signature
    });
    return result;
  }

  /**
   * Validates recovery signature.
   * @param email
   */
  public async validateInviteId(inviteId: string): Promise<FetchResult<IGraphQLResponse<string>>> {
    let result = await this.graphQL.query<IGraphQLResponse<string>>(INVITE_ID_VALIDATION, {
      inviteId: inviteId
    });
    return result;
  }

  /**
   * Gets info about user invite.
   */
  public async getInviteInfo(inviteId: string): Promise<Invite> {
    let result = await this.graphQL.query<IGraphQLResponse<Invite>>(INVITE_INFO, {
      inviteId: inviteId
    });
    if (result.data == undefined
      || result.data.response == undefined
      || result.data.response.data == undefined
      || result.data.response.isSuccess == false) {
      Promise.reject(new FatalError('Could not get boards of the user.'));
    }

    return result.data?.response.data!;
  }

  /**
   * Gets info about user invite.
   */
  public async acceptInvite(inviteId: string, password: string, username: string): Promise<boolean | string> {
    let result = await this.graphQL.mutate<IGraphQLResponse<boolean>>(ACCEPT_INVITE, {
      inviteId: inviteId,
      password: password,
      username: username
    });
    if (result.data == undefined
      || result.data.response == undefined
      || result.data.response.data == undefined
      || result.data.response.isSuccess == false) {
      return result.data?.response.message!;
    }

    return true;
  }

}
